import React, { useEffect, useMemo, useState } from "react";
import { getCurrentUser } from "../../auth";
import { getActiveSchoolYear } from "../../services/sisAcademicService";
import { loadGradeSections } from "../../services/sectionsService";
import { listTeacherUsers, teacherWorkloadDocId } from "../../services/teacherWorkloadService";
import { rebuildClassSchedulesCurrent } from "../../services/classScheduleService";
import { db } from "../../services/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";

function norm(s) {
  return String(s || "").trim();
}
function normEmail(s) {
  return String(s || "").trim().toLowerCase();
}
function emailKey(email) {
  return normEmail(email).replace(/[^a-z0-9]+/g, "_");
}

function emptyWorkload() {
  return { advisories: [], classes: [] };
}

export default function TeacherWorkloads() {
  const me = getCurrentUser();
  const [campus, setCampus] = useState(String(me?.campus || "gapan").toLowerCase());
  const [schoolYear, setSchoolYear] = useState("");
  const [teachers, setTeachers] = useState([]);
  const [teacherUid, setTeacherUid] = useState("");
  const [teacherEmail, setTeacherEmail] = useState("");
  const [gradeSections, setGradeSections] = useState({});
  const [workload, setWorkload] = useState(emptyWorkload());
  const [info, setInfo] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const sy = await getActiveSchoolYear(campus);
      setSchoolYear(sy || "");
    })();
  }, [campus]);

  useEffect(() => {
    (async () => {
      const list = await listTeacherUsers();
      setTeachers(list);
      if (!teacherUid && list.length) {
        setTeacherUid(String(list[0].uid || list[0].id || ""));
        setTeacherEmail(normEmail(list[0].email || ""));
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const map = await loadGradeSections(campus);
      setGradeSections(map || {});
    })();
  }, [campus]);

  useEffect(() => {
    (async () => {
      setInfo("");
      if (!teacherUid || !schoolYear) { setWorkload(emptyWorkload()); return; }
      const id = teacherWorkloadDocId({ uid: teacherUid, email: teacherEmail, campus, schoolYear });
      const ref = doc(db, "teacher_workloads", id);
      const snap = await getDoc(ref);
      if (!snap.exists()) { setWorkload(emptyWorkload()); return; }
      const d = snap.data() || {};
      setWorkload({
        advisories: Array.isArray(d.advisories) ? d.advisories : [],
        classes: Array.isArray(d.classes) ? d.classes : []
      });
    })();
  }, [teacherUid, teacherEmail, campus, schoolYear]);

  const gradeList = useMemo(() => Object.keys(gradeSections || {}), [gradeSections]);

  function toggleAdvisory(grade, section) {
    setWorkload(prev => {
      const items = Array.isArray(prev.advisories) ? prev.advisories : [];
      const key = `${grade}__${section}`;
      const exists = items.some(x => `${x.grade}__${x.section}` === key);
      return {
        ...prev,
        advisories: exists ? items.filter(x => `${x.grade}__${x.section}` !== key) : items.concat([{ grade, section }])
      };
    });
  }

  function addClassRow() {
    setWorkload(prev => ({
      ...prev,
      classes: (Array.isArray(prev.classes) ? prev.classes : []).concat([{
        grade: "",
        section: "",
        subject: "",
        schedule: "",
        room: "",
        gradebookUrl: ""
      }])
    }));
  }

  function updateClassRow(idx, patch) {
    setWorkload(prev => {
      const arr = Array.isArray(prev.classes) ? prev.classes.slice() : [];
      arr[idx] = { ...(arr[idx] || {}), ...patch };
      return { ...prev, classes: arr };
    });
  }

  function removeClassRow(idx) {
    setWorkload(prev => {
      const arr = Array.isArray(prev.classes) ? prev.classes.slice() : [];
      arr.splice(idx, 1);
      return { ...prev, classes: arr };
    });
  }

  async function save() {
    if (!teacherUid || !schoolYear) return;
    setSaving(true);
    setInfo("Saving…");
    try {
      const id = teacherWorkloadDocId({ uid: teacherUid, email: teacherEmail, campus, schoolYear });
      const ref = doc(db, "teacher_workloads", id);
      const currentId = `${String(teacherUid)}__${String(campus || "").trim().toLowerCase()}`;
      const currentRef = doc(db, "teacher_workloads_current", currentId);
      const currentIdEmail = `${emailKey(teacherEmail)}__${String(campus || "").trim().toLowerCase()}`;
      const currentRefEmail = doc(db, "teacher_workloads_current", currentIdEmail);
      const payload = {
        uid: teacherUid,
        email: normEmail(teacherEmail || ""),
        campus: String(campus || "").trim().toLowerCase(),
        schoolYear: norm(schoolYear),
        advisories: (Array.isArray(workload.advisories) ? workload.advisories : []).map(a => ({ grade: norm(a?.grade), section: norm(a?.section) })).filter(a => a.grade && a.section),
        classes: (Array.isArray(workload.classes) ? workload.classes : []).map(c => ({
          grade: norm(c?.grade),
          section: norm(c?.section),
          subject: norm(c?.subject),
          schedule: norm(c?.schedule),
          room: norm(c?.room),
          gradebookUrl: norm(c?.gradebookUrl)
        })).filter(c => c.grade || c.section || c.subject || c.gradebookUrl),
        updatedAt: new Date().toISOString(),
        updatedBy: String(me?.email || "")
      };
      await setDoc(ref, payload, { merge: true });
      await setDoc(currentRef, { ...payload, sourceDocId: id }, { merge: true });
      await setDoc(currentRefEmail, { ...payload, sourceDocId: id }, { merge: true });
      const impacted = [
        ...(payload.classes || []).map(c => ({ gradeLevel: c.grade, section: c.section })),
        ...(payload.advisories || []).map(a => ({ gradeLevel: a.grade, section: a.section }))
      ];
      try {
        await rebuildClassSchedulesCurrent({ campus: payload.campus, schoolYear: payload.schoolYear, sections: impacted });
      } catch {
        void 0;
      }
      let confirm = null;
      try {
        const snap = await getDoc(currentRef);
        confirm = snap.exists() ? snap.data() : null;
      } catch {
        confirm = null;
      }
      const classCount = Array.isArray(confirm?.classes) ? confirm.classes.length : 0;
      const advCount = Array.isArray(confirm?.advisories) ? confirm.advisories.length : 0;
      setInfo(`Saved. Doc: ${id} | Current: ${currentId} | CurrentEmail: ${currentIdEmail} | Advisories: ${advCount} | Classes: ${classCount}`);
    } catch (e) {
      const code = String(e?.code || "");
      const msg = String(e?.message || "Failed to save.");
      setInfo(code ? `Failed to save (${code}): ${msg}` : `Failed to save: ${msg}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="container mt-4">
      <h3 className="mb-3">Teacher Workloads</h3>

      <div className="card p-3 mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-md-3">
            <label className="form-label">Campus</label>
            <select className="form-select" value={campus} onChange={e => setCampus(e.target.value)}>
              <option value="gapan">Gapan</option>
              <option value="bulacan">Bulacan</option>
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">School Year</label>
            <input className="form-control" value={schoolYear} onChange={e => setSchoolYear(e.target.value)} placeholder="2026-27" />
          </div>
          <div className="col-md-4">
            <label className="form-label">Teacher</label>
            <select
              className="form-select"
              value={teacherUid}
              onChange={e => {
                const uid = e.target.value;
                setTeacherUid(uid);
                const t = teachers.find(x => String(x.uid || x.id || "") === uid);
                setTeacherEmail(normEmail(t?.email || ""));
              }}
            >
              {teachers.map(t => (
                <option key={String(t.uid || t.id)} value={String(t.uid || t.id)}>
                  {String(t.email || t.uid || t.id)}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-2 d-flex gap-2">
            <button className="btn btn-primary w-100" onClick={save} disabled={saving || !teacherUid || !schoolYear}>Save</button>
          </div>
        </div>
        {info && <div className="alert alert-info mt-3 mb-0">{info}</div>}
      </div>

      <div className="card p-3 mb-3">
        <h5 className="mb-3">Advisories (for Attendance + Class List)</h5>
        {gradeList.length === 0 ? (
          <div className="text-muted">No grade/sections configured yet. Use Admin → Sections Manager.</div>
        ) : (
          <div className="row g-3">
            {gradeList.map(g => (
              <div className="col-12 col-md-6 col-lg-4" key={g}>
                <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 12 }}>
                  <div style={{ fontWeight: 900, marginBottom: 10 }}>{g}</div>
                  <div className="d-flex flex-wrap gap-2">
                    {(gradeSections[g] || []).map(sec => {
                      const checked = (workload.advisories || []).some(x => String(x.grade) === String(g) && String(x.section) === String(sec));
                      return (
                        <label key={sec} className="form-check" style={{ margin: 0 }}>
                          <input className="form-check-input" type="checkbox" checked={checked} onChange={() => toggleAdvisory(g, sec)} />
                          <span className="ms-1">{sec}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card p-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h5 className="mb-0">Class Program (Subject Schedule)</h5>
          <button className="btn btn-outline-primary btn-sm" onClick={addClassRow}>Add Class</button>
        </div>
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 160 }}>Grade</th>
                <th style={{ width: 160 }}>Section</th>
                <th style={{ minWidth: 220 }}>Subject</th>
                <th style={{ width: 220 }}>Schedule</th>
                <th style={{ width: 140 }}>Room</th>
                <th style={{ minWidth: 320 }}>Gradebook Link (Google Sheets)</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {(workload.classes || []).map((c, idx) => (
                <tr key={idx}>
                  <td>
                    <select className="form-select form-select-sm" value={c.grade || ""} onChange={e => updateClassRow(idx, { grade: e.target.value, section: "" })}>
                      <option value="">Select</option>
                      {gradeList.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </td>
                  <td>
                    <select className="form-select form-select-sm" value={c.section || ""} onChange={e => updateClassRow(idx, { section: e.target.value })} disabled={!c.grade}>
                      <option value="">Select</option>
                      {(gradeSections[c.grade] || []).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td>
                    <input className="form-control form-control-sm" value={c.subject || ""} onChange={e => updateClassRow(idx, { subject: e.target.value })} />
                  </td>
                  <td>
                    <input className="form-control form-control-sm" value={c.schedule || ""} onChange={e => updateClassRow(idx, { schedule: e.target.value })} placeholder="Mon/Wed 8:00-9:00" />
                  </td>
                  <td>
                    <input className="form-control form-control-sm" value={c.room || ""} onChange={e => updateClassRow(idx, { room: e.target.value })} />
                  </td>
                  <td>
                    <input className="form-control form-control-sm" value={c.gradebookUrl || ""} onChange={e => updateClassRow(idx, { gradebookUrl: e.target.value })} placeholder="https://docs.google.com/spreadsheets/..." />
                  </td>
                  <td className="text-end">
                    <button className="btn btn-sm btn-outline-danger" onClick={() => removeClassRow(idx)}>Remove</button>
                  </td>
                </tr>
              ))}
              {(workload.classes || []).length === 0 && (
                <tr><td colSpan="7" className="text-center text-muted py-3">No classes added</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="small text-muted mt-2">
          Teachers will see these in Teacher Portal → Class Program, and they can open the Gradebook link directly.
        </div>
      </div>
    </div>
  );
}
